import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import CultService from '../../Services/CultService';
import { HelperTexts } from '../../Utils/helper/HelperTexts';
import toast from 'react-hot-toast';
import PageHeading from '../../Components/PageHeading';
import PageLoader from '../../Components/PageLoader';
import { ExpandIcon } from '../../Components/Icons/Icons';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Grid,
	Box,
	ButtonGroup,
	Button,
} from '@mui/material';

import { AppColors } from '../../Utils/helper/AppColors';
import { FiChevronDown } from 'react-icons/fi';
import { formatAndSortInsights, getColorSettings } from '../../Utils/helper/commonUtils';
import DataNotFound from '../../Components/DataNotFound.js';
import { theme } from 'antd';
import SelectCampaign from '../../Components/SelectCampaign.js';
import CampaignEmptyState from '../../Components/CampaignEmptyState.js';
import { InsightsTable } from './Components/InsightsTable.js';

const COLORS = {
	Neutral: AppColors.STATUS_YELLOW,
	Positive: AppColors.STATUS_GREEN,
	Negative: AppColors.STATUS_RED,
};

export default function MoodInsights() {
	const [loading, setLoading] = useState(false);
	const company = useSelector((state) => state?.app?.company);
	const [details, setDetails] = useState(null);
	const [shortDetails, setShortDetails] = useState(null);
	const [selectedInsight, setSelectedInsight] = useState(null); // State to track selected insight
	const [campaignId, setCampaignId] = useState(null);

	const [viewType, setViewType] = useState(0);

   // Detailed details of a Campaign
	const getDetails = async () => {
		setLoading(true);
		try {
			let res = await CultService.getPositiveInsights({ params: { companyId: company?._id , campaignId : campaignId} });
			setDetails(res?.data?.data);
		} catch (err) {
			toast.error(HelperTexts?.SOMETHING_WENT_WRONG);
		} finally {
			setLoading(false);
		}
	};

	// Short details of campaign :
	const getShortDetails = async () => {
		setLoading(true);
		try {
			let res = await CultService.getShortCampaignDetails({ params: { campaignId : campaignId} });
			// console.log('Short Details:', res);
			setShortDetails(res?.data?.data?.encouragingVibes);
		} catch (err) {
			toast.error(HelperTexts?.SOMETHING_WENT_WRONG);
		} finally {
			setLoading(false);
		}
	};

	const list = useMemo(() => formatAndSortInsights(details), [details]);

	const handleSelectCampaign = (i, option) => {
		setCampaignId(option?.item?._id);
	};

	useEffect(() => {
		if (company && campaignId) { getDetails(); getShortDetails(); }
	}, [company , campaignId ]);

	useEffect(() => {
		if (list?.length > 0) {
			setSelectedInsight(list[0]); // Set the first insight as selected by default
		}
	}, [list]);

	const localSettingColors = getColorSettings();

	return (
		<Stack sx={{ padding: 2 , gap: 1 }}>
			{loading && <PageLoader />}
			<PageHeading title="Encouraging Vibes" subTitle="Insights into profound employee dynamics" />

   			<Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
			<SelectCampaign handleSelectCampaign={handleSelectCampaign} width={'488px'} />

           
            { campaignId && <ButtonGroup>
				<Button
					variant={viewType === 0 ? 'contained' : 'outlined'}
					color='info'
					onClick={() => setViewType(0)}
				>
					Detailed
				</Button>
				<Button
					variant={viewType === 1 ? 'contained' : 'outlined'}
					color='info'
					onClick={() => setViewType(1)}
				>
					Summary
				</Button>
			</ButtonGroup> }

			</Stack>

			<Box py={2}>
				
								
				{!campaignId && <CampaignEmptyState text="Select a campaign to view campaign insights." />}
				
				{list?.length === 0 && campaignId ? (
					<Stack
						sx={{ backgroundColor: AppColors.WHITE, height: 'calc(100vh - 180px)', borderRadius: '8px' }}
						alignItems="center"
						justifyContent="center"
						height="100%"
					>
						<DataNotFound />
					</Stack>
				) : (

					// Render type 1 (Overall)
					 (viewType === 0) ? (
					<Grid container spacing={{ sm: 10, md: 1 }}>
						{/* Left Side: List of Insights */}
						<Grid
							item
							xs={12}
							sm={12}
							md={3.5}
							sx={{
								height: 'calc(100vh - 180px)',
								overflowY: 'auto',
							}}
						>
							<Stack gap={1}>
								{list?.map((insight, index) => (
									<Stack
										key={index}
										direction="column"
										justifyContent="space-between"
										alignItems="flex-start"
										py={'12px'}
										px={'15px'}
										gap={'10px'}
										height={'80px'}
										sx={(theme) => ({
											backgroundColor:
												selectedInsight?.name === insight.name ? theme.palette.primary.lightest_shade : AppColors.WHITE,
											borderRadius: '15px',
											border: `1px solid ${
												selectedInsight?.name === insight.name ? theme.palette.primary.lighter_shade : AppColors.BORDER_SECONDARY
											}`,
											cursor: 'pointer',
											// width: '100%',
											'&:hover': { backgroundColor: theme.palette.primary.lightest_shade },
										})}
										onClick={() => setSelectedInsight(insight)} // Set selected insight
									>
										{/* Name and icon */}
										<Stack
											direction="row"
											justifyContent="space-between"
											sx={{ width: '100%' }} // Added width 100% to ensure proper alignment
										>
											<Typography
												textTransform="capitalize"
												sx={(theme) => ({
													color:
														selectedInsight?.name === insight.name
															? theme.palette.primary.darker_primary
															: AppColors.HEADING_TITLE,
													textOverflow: 'ellipsis',
													overflow: 'hidden',
													whiteSpace: 'nowrap',
													maxWidth: '300px',
													minWidth: '200px',
												})}
												fontFamily={'Poppins'}
												fontSize={14}
												fontWeight={600}
												lineHeight={'150%'}
												letterSpacing={'0%'}
											>
												{insight.name}
											</Typography>

											<Stack direction="row" ml={'auto'}>
												<ExpandIcon height={19} width={19} color={localSettingColors.fontColor} />
											</Stack>
										</Stack>

										{/* Report Tag */}
										<Stack
											direction="row"
											alignItems="center"
											justifyContent="center"
											sx={(theme) => ({
												backgroundColor:
													selectedInsight?.name === insight.name ? AppColors.WHITE : theme.palette.primary.lightest_shade,
												color:
													selectedInsight?.name === insight.name
														? theme.palette.primary.lightest_shade
														: theme.palette.primary.darker_primary,
												borderRadius: '30px',
												border: `1px solid ${AppColors.BORDER_TERTIARY}`,

												px: 2,
												py: '6px',
											})}
										>
											<Typography
												fontSize={12}
												fontWeight={500}
												fontFamily={'Poppins'}
												lineHeight={'121%'}
												sx={(theme) => ({ color: theme.palette.primary.darker_primary })}
											>
												Reports: {insight.total_users}
											</Typography>
										</Stack>
									</Stack>
								))}
							</Stack>
						</Grid>

						{/* Right Side: Details of Selected Insight */}
						<Grid item xs={12} sm={12} md={8.5}>
							{selectedInsight ? (
								<Stack gap={1}>
									{/* Container for Topic and Subtopics */}
									<Stack
										direction="column"
										gap={2}
										p={2}
										sx={(theme) => ({
											backgroundColor: AppColors.WHITE,
											borderRadius: 2,
											border: `1px solid ${theme.palette.primary.lighter_shade}`,
											boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',

											height: 'calc(100vh - 180px)',
											overflowY: 'auto',
										})}
									>
										{/* Topic Heading */}
										<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginRight: '15px' }}>
											<Typography
												textTransform="capitalize"
												fontFamily={'Poppins'}
												fontSize={18}
												fontWeight={600}
												lineHeight={'150%'}
												color={AppColors.HEADING_TITLE}
											>
												{selectedInsight?.name}
											</Typography>

											{/* Report Tag need cheage in tag fucture */}
											<Stack
												direction="row"
												alignItems="center"
												justifyContent="flex-start"
												sx={(theme) => ({
													backgroundColor: theme.palette.primary.lightest_shade,
													color: theme.palette.primary.darker_primary,
													borderRadius: '30px',
													border: `1px solid ${theme.palette.primary.lighter_shade}`,
													px: 2,
													py: '6px',
													width: 'fit-content',
												})}
											>
												<Typography
													fontWeight={500}
													fontFamily={'Poppins'}
													lineHeight={'121%'}
													fontSize={12}
													sx={(theme) => ({ color: theme.palette.primary.darker_primary })}
												>
													Reports: {selectedInsight.total_users}
												</Typography>
											</Stack>
										</Stack>

										{/* Subtopics */}
										{selectedInsight?.subTopic?.map((sub, subIndex) => (
											<Stack
												key={subIndex}
												direction="column"
												gap={1}
												sx={(theme) => ({
													backgroundColor: AppColors.WHITE,
													borderRadius: '10px',
													border: `1px solid ${theme.palette.primary.light_shade}`,
												})}
											>
												{/* Subtopic Details */}
												<TableContainer sx={{ border: 'none' }}>
													{/* First Header: Subtopic Name and Report Count */}
													<Stack
														direction="row"
														justifyContent="space-between"
														alignItems="center"
														sx={(theme) => ({
															padding: '8px 16px',
															borderRadius: '8px 8px 0 0',
															borderBottom: `1px solid ${theme.palette.primary.light_shade}`,
														})}
													>
														<Typography
															textTransform="capitalize"
															fontWeight={600}
															fontFamily={'Poppins'}
															lineHeight={'150%'}
															fontSize={16}
														>
															{sub?.name}
														</Typography>

														<Stack
															direction="row"
															alignItems="center"
															justifyContent="flex-start"
															sx={(theme) => ({
																backgroundColor: theme.palette.primary.lightest_shade,
																color: theme.palette.primary.darker_primary,
																borderRadius: '30px',
																border: `1px solid ${theme.palette.primary.lighter_shade}`,
																px: 2,
																py: '6px',
																width: 'fit-content',
															})}
														>
															<Typography
																fontWeight={500}
																fontFamily={'Poppins'}
																lineHeight={'121%'}
																fontSize={12}
																sx={(theme) => ({ color: theme.palette.primary.darker_primary })}
															>
																Reports: {sub?.total_users}
															</Typography>
														</Stack>
													</Stack>

													{/* Table */}
													<Table sx={{ width: '96%', margin: '0 auto' }}>
														{/* Second Header: Column Headers */}
														<TableHead
															sx={(theme) => ({
																borderBottom: `1px solid ${theme.palette.primary.lighter_shade}`,
																'& .MuiTableCell-root': {
																	paddingLeft: 0,
																	paddingY: '8px',
																	fontWeight: 600,
																	fontFamily: 'Poppins',
																	lineHeight: '150%',
																	fontSize: 14,
																	color: AppColors.HEADING_TITLE2,
																},
															})}
														>
															<TableRow>
																<TableCell width="30.50%">Insights</TableCell>
																{/* <TableCell width="69.50%">Recommended Action</TableCell> */}
															</TableRow>
														</TableHead>

														{/* Table Body */}
														<TableBody sx={{ '& .MuiTableCell-root': { paddingLeft: 0, verticalAlign: 'top' } }}>
															{sub?.cluster?.map((mention, mentionIndex) => (
																<TableRow key={mentionIndex}>
																	<TableCell>
																		<Typography
																			sx={{
																				paddingLeft: 0,
																				fontWeight: 400,
																				fontFamily: 'Poppins',
																				lineHeight: '150%',
																				fontSize: '14px',
																				color: AppColors.HEADING_TITLE3,
																			}}
																		>
																			{mention?.cluster?.issue}
																		</Typography>
																	</TableCell>
																	{/* <TableCell>
																	<Typography
																		sx={{
																			paddingLeft: 0,
																			fontWeight: 400,
																			fontFamily: 'Poppins',
																			lineHeight: '150%',
																			fontSize: '14px',
																			color: AppColors.HEADING_TITLE3,
																		}}
																	>
																		{mention?.cluster?.solution}
																	</Typography>
																</TableCell> */}
																</TableRow>
															))}
														</TableBody>
													</Table>
												</TableContainer>
											</Stack>
										))}
									</Stack>
								</Stack>
							) : (
								<Stack alignItems="center" justifyContent="center" height="100%">
									<Typography variant="h6" color={AppColors.TEXT_SECONDARY}>
										{/* Select an insight to view details */}
									</Typography>
								</Stack>
							)}
						</Grid>
					</Grid>
					) : (
					// Render type 2 (By Campaign)
					<Stack>
						<InsightsTable data={shortDetails} />
					</Stack>
					)
				
				)}
			</Box>
		</Stack>
	);
}
